import { withAuthenticationRequired } from '@auth0/auth0-react'
import { CircularProgress, Stack } from '@mui/material'
import { AppointmentProvider, useAppointment, useNotification, useTrackError } from 'hooks'
import { useAppointmentsService } from 'hooks/services'
import { useAppointmentCalendarIntegrations } from 'hooks/useAppointmentCalendarIntegrations'
import { NotFound } from 'modules/NotFound'
import { useState } from 'react'
import { Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { isCalendarIntegration } from 'util/calendar-integrations'
import { AppointmentCancellationReason, AppointmentRescheduleReason, RouteParameters, ToastSubType } from '../../constants'
import { EditAppointmentForm, EditAppointmentFormData } from './EditAppointmentForm'
import { EditAppointmentSuccess } from './EditAppointmentSuccess'
import { EditAppointmentWithIntegration } from './EditAppointmentWithIntegration'
import { toUtcIsoString } from './utils'

function EditAppointmentWithoutProvider() {
  const navigate = useNavigate()
  const { [RouteParameters.APPOINTMENT_GID]: appointmentGid, [RouteParameters.CUSTOMER_GID]: customerGid } = useParams()

  const [step, setStep] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<EditAppointmentFormData>({
    action: '',
    rescheduledDate: '',
    rescheduledTime: '',
  })
  const { notify } = useNotification()
  const trackError = useTrackError()

  const { appointment } = useAppointment()
  const { cancelAppointment, rescheduleAppointment } = useAppointmentsService()
  const { data: appointmentCalendarIntegrations, isLoading } = useAppointmentCalendarIntegrations(
    { appointmentGid: appointmentGid ?? '' },
    !!appointmentGid
  )

  const hasCalendarIntegrations = appointmentCalendarIntegrations?.some((integration) => isCalendarIntegration(integration.type))

  const handleSubmit = async (formData: EditAppointmentFormData) => {
    if (hasCalendarIntegrations) {
      setStep(1)
      return
    }

    if (!appointment) return

    try {
      if (formData.action === 'cancel') {
        setIsSubmitting(true)
        await cancelAppointment({ appointmentId: appointment.id, reason: AppointmentCancellationReason.OTHER })
        navigate({ pathname: `${location.pathname}/success`, search: `?action=cancel` })
      } else if (formData.action === 'reschedule') {
        setIsSubmitting(true)
        const rescheduledDateTime = `${formData.rescheduledDate} ${formData.rescheduledTime}`
        await rescheduleAppointment({
          appointmentIdRescheduled: appointment.id,
          reason: AppointmentRescheduleReason.OTHER,
          newAppointmentRequest: {
            country: appointment.country,
            customer_salesforce_id: appointment.customer.salesforceId,
            scheduled_customer_timezone: appointment.scheduled_customer_timezone ?? '',
            duration_in_minutes: appointment.duration_in_minutes,
            journey: appointment.journey,
            opportunity_gid: appointment.opportunity_gid ?? '',
            resource_ids: appointment.resources?.map((resource) => resource.id) ?? [],
            is_manual: appointment.is_manual,
            shop_gid: appointment.shop_gid ?? '',
            start_date: (toUtcIsoString(rescheduledDateTime) ?? '') as unknown as Date,
            ...(appointment.title ? { title: appointment.title } : {}),
            type: appointment.type.appointment_type,
            ...(appointment.description ? { description: appointment.description } : {}),
          },
        })
        navigate({ pathname: `${location.pathname}/success`, search: `?action=reschedule` })
      }
    } catch (error: any) {
      notify(
        {
          message: 'Sorry, there was an error while editing this appointment',
          type: formData.action === 'cancel' ? ToastSubType.CANCELLATION_FAILURE : ToastSubType.RESCHEDULE_FAILURE,
        },
        'error'
      )
      trackError(error?.toJSON())
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center">
        <CircularProgress />
      </Stack>
    )
  }
  if (!customerGid || !appointmentGid || !appointment) {
    return null
  }

  if (step === 1) {
    return <EditAppointmentWithIntegration action={formData.action} />
  }

  return (
    <EditAppointmentForm
      onSubmit={handleSubmit}
      allowSimpleEditing={!hasCalendarIntegrations}
      formData={formData}
      setFormData={setFormData}
      isSubmitting={isSubmitting}
    />
  )
}

export const EditAppointment = withAuthenticationRequired(() => (
  <AppointmentProvider>
    <Routes>
      <Route element={<EditAppointmentWithoutProvider />} path="/" />
      <Route element={<EditAppointmentSuccess />} path="success" />
      <Route element={<NotFound />} path="*" />
    </Routes>
  </AppointmentProvider>
))
